/**
 * domainMap.js
 * Canonical domain mappings for all 21 core Homy modules.
 * Binds each domain entity to its canonical glyph, semantic color token,
 * dark mode hex color, and composite action badge attributes.
 */

export const DOMAINS = [
  "properties",
  "rentals",
  "maintenance",
  "payments",
  "calendar",
  "tasks",
  "support",
  "applications",
  "trust",
  "homysign",
  "inbox",
  "listings",
  "marketplace",
  "memberships",
  "organizations",
  "databases",
  "comments",
  "landingPage",
  "settings",
  "core",
  "public",
];

export const domainMap = {
  properties: {
    key: "properties",
    name: "Properties",
    glyphName: "BuildingPropertyIcon",
    colorClass: "text-cyan-400",
    hexColor: "#38bdf8",
    bgClass: "bg-cyan-500/10",
    borderClass: "border-cyan-500/20",
    badgeColorClass: "text-cyan-400",
    defaultAction: "add",
    compositeAction: "properties:create",
    description: "Multi-story modular bento architecture with windows and entrance",
  },
  rentals: {
    key: "rentals",
    name: "Rentals / Leases",
    glyphName: "KeyLeaseIcon",
    colorClass: "text-emerald-400",
    hexColor: "#10b981",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/20",
    badgeColorClass: "text-emerald-400",
    defaultAction: "add",
    compositeAction: "rentals:create",
    description: "Geometric spatial key paired with lease document notch",
  },
  maintenance: {
    key: "maintenance",
    name: "Maintenance",
    glyphName: "WrenchServiceIcon",
    colorClass: "text-rose-400",
    hexColor: "#fb7185",
    bgClass: "bg-rose-500/10",
    borderClass: "border-rose-500/20",
    badgeColorClass: "text-rose-400",
    defaultAction: "add",
    compositeAction: "maintenance:create",
    description: "Precision angled spanner with calibration gear notch",
  },
  payments: {
    key: "payments",
    name: "Payments",
    glyphName: "CreditCardPaymentIcon",
    colorClass: "text-emerald-500",
    hexColor: "#10b981",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/20",
    badgeColorClass: "text-emerald-500",
    defaultAction: "add",
    compositeAction: "payments:create",
    description: "EMV biometric payment card with chip and telemetry trace",
  },
  calendar: {
    key: "calendar",
    name: "Calendar / Visits",
    glyphName: "CalendarClockIcon",
    colorClass: "text-cyan-400",
    hexColor: "#38bdf8",
    bgClass: "bg-cyan-500/10",
    borderClass: "border-cyan-500/20",
    badgeColorClass: "text-cyan-400",
    defaultAction: "schedule",
    compositeAction: "calendar:create",
    description: "Bento calendar grid with corner clock sector dial",
  },
  tasks: {
    key: "tasks",
    name: "Tasks",
    glyphName: "CheckSquareTaskIcon",
    colorClass: "text-amber-400",
    hexColor: "#f59e0b",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/20",
    badgeColorClass: "text-amber-400",
    defaultAction: "add",
    compositeAction: "tasks:create",
    description: "Rounded bento check square with sharp precision mark",
  },
  support: {
    key: "support",
    name: "Support",
    glyphName: "LifebuoySupportIcon",
    colorClass: "text-purple-400",
    hexColor: "#c084fc",
    bgClass: "bg-purple-500/10",
    borderClass: "border-purple-500/20",
    badgeColorClass: "text-purple-400",
    defaultAction: "add",
    compositeAction: "support:create",
    description: "Spatial lifebuoy ring with 4 quadrant grip bands",
  },
  applications: {
    key: "applications",
    name: "Applications",
    glyphName: "ClipboardDossierIcon",
    colorClass: "text-blue-400",
    hexColor: "#60a5fa",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/20",
    badgeColorClass: "text-blue-400",
    defaultAction: "add",
    compositeAction: "applications:create",
    description: "Applicant dossier clipboard with top clamp and structured lines",
  },
  trust: {
    key: "trust",
    name: "HomyTrust",
    glyphName: "ShieldTrustIcon",
    colorClass: "text-emerald-400",
    hexColor: "#10b981",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/20",
    badgeColorClass: "text-emerald-400",
    defaultAction: "check",
    compositeAction: "trust:create",
    description: "Security shield with center trust check crest",
  },
  homysign: {
    key: "homysign",
    name: "HomySign",
    glyphName: "FileSignatureIcon",
    colorClass: "text-teal-400",
    hexColor: "#2dd4bf",
    bgClass: "bg-teal-500/10",
    borderClass: "border-teal-500/20",
    badgeColorClass: "text-teal-400",
    defaultAction: "add",
    compositeAction: "homysign:create",
    description: "Folded-corner legal document with digital signature flourish",
  },
  inbox: {
    key: "inbox",
    name: "Inbox",
    glyphName: "InboxCockpitIcon",
    colorClass: "text-primary",
    hexColor: "#c25237",
    bgClass: "bg-primary/10",
    borderClass: "border-primary/20",
    badgeColorClass: "text-primary",
    defaultAction: "add",
    compositeAction: "inbox:create",
    description: "Tiered tray stack with telemetry notification node",
  },
  listings: {
    key: "listings",
    name: "Listings",
    glyphName: "LayoutGridListingsIcon",
    colorClass: "text-info",
    hexColor: "#38bdf8",
    bgClass: "bg-info/10",
    borderClass: "border-info/20",
    badgeColorClass: "text-info",
    defaultAction: "add",
    compositeAction: "listings:create",
    description: "4-cell bento card showcase with focal hero tile",
  },
  marketplace: {
    key: "marketplace",
    name: "Marketplace",
    glyphName: "StoreMarketplaceIcon",
    colorClass: "text-amber-500",
    hexColor: "#f59e0b",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/20",
    badgeColorClass: "text-amber-500",
    defaultAction: "add",
    compositeAction: "marketplace:create",
    description: "Modern storefront with scallop awning and showcase bay",
  },
  memberships: {
    key: "memberships",
    name: "Memberships",
    glyphName: "CrownTierIcon",
    colorClass: "text-yellow-400",
    hexColor: "#facc15",
    bgClass: "bg-yellow-500/10",
    borderClass: "border-yellow-500/20",
    badgeColorClass: "text-yellow-400",
    defaultAction: "upgrade",
    compositeAction: "memberships:upgrade",
    description: "Multi-point sovereign crown with geometric facet gems",
  },
  organizations: {
    key: "organizations",
    name: "Organizations",
    glyphName: "BuildingOrgIcon",
    colorClass: "text-foreground",
    hexColor: "#f9f8f6",
    bgClass: "bg-stone-500/10",
    borderClass: "border-stone-500/20",
    badgeColorClass: "text-foreground",
    defaultAction: "add",
    compositeAction: "organizations:create",
    description: "Dual corporate towers linked by spatial skybridge",
  },
  databases: {
    key: "databases",
    name: "Databases",
    glyphName: "DatabaseStackIcon",
    colorClass: "text-violet-400",
    hexColor: "#a78bfa",
    bgClass: "bg-violet-500/10",
    borderClass: "border-violet-500/20",
    badgeColorClass: "text-violet-400",
    defaultAction: "add",
    compositeAction: "databases:create",
    description: "3-tier cylindrical database stack with telemetry pulse",
  },
  comments: {
    key: "comments",
    name: "Comments",
    glyphName: "MessageThreadIcon",
    colorClass: "text-sky-300",
    hexColor: "#7dd3fc",
    bgClass: "bg-sky-500/10",
    borderClass: "border-sky-500/20",
    badgeColorClass: "text-sky-300",
    defaultAction: "add",
    compositeAction: "comments:create",
    description: "Conversation speech bubble with nested thread reply notch",
  },
  landingPage: {
    key: "landingPage",
    name: "Landing Page",
    glyphName: "GlobeWebIcon",
    colorClass: "text-cyan-400",
    hexColor: "#38bdf8",
    bgClass: "bg-cyan-500/10",
    borderClass: "border-cyan-500/20",
    badgeColorClass: "text-cyan-400",
    defaultAction: "edit",
    compositeAction: "landingPage:edit",
    description: "Latitudinal globe mesh with spatial meridian arcs",
  },
  settings: {
    key: "settings",
    name: "Settings",
    glyphName: "SlidersSettingsIcon",
    colorClass: "text-muted-foreground",
    hexColor: "#8c8a88",
    bgClass: "bg-stone-500/10",
    borderClass: "border-stone-500/20",
    badgeColorClass: "text-muted-foreground",
    defaultAction: "edit",
    compositeAction: "settings:config",
    description: "Precision horizontal balance sliders with stepped thumb nodes",
  },
  core: {
    key: "core",
    name: "Core / User",
    glyphName: "UserCockpitIcon",
    colorClass: "text-primary",
    hexColor: "#c25237",
    bgClass: "bg-primary/10",
    borderClass: "border-primary/20",
    badgeColorClass: "text-primary",
    defaultAction: "add",
    compositeAction: "core:invite",
    description: "Operator profile silhouette with authenticated badge ring",
  },
  public: {
    key: "public",
    name: "Public",
    glyphName: "CompassPublicIcon",
    colorClass: "text-blue-400",
    hexColor: "#60a5fa",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/20",
    badgeColorClass: "text-blue-400",
    defaultAction: "search",
    compositeAction: "public:explore",
    description: "Precision navigation compass rose with north-seeking needle",
  },
};

export function getDomainConfig(domain) {
  if (!domain || typeof domain !== "string") return null;
  return Object.prototype.hasOwnProperty.call(domainMap, domain)
    ? domainMap[domain]
    : null;
}

export function getDomainColor(domain) {
  if (!domain || typeof domain !== "string") return "text-foreground";
  return Object.prototype.hasOwnProperty.call(domainMap, domain)
    ? domainMap[domain].colorClass || "text-foreground"
    : "text-foreground";
}

export function getDomainHex(domain) {
  if (!domain || typeof domain !== "string") return "#f9f8f6";
  return Object.prototype.hasOwnProperty.call(domainMap, domain)
    ? domainMap[domain].hexColor || "#f9f8f6"
    : "#f9f8f6";
}
